import { useState, useEffect, Fragment } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const numFilter = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

// trip_date is a DATE string ('YYYY-MM-DD'); avoid new Date() UTC parsing shifting the day
const formatDate = (dateStr) => {
  const m = typeof dateStr === 'string' && dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${parseInt(m[2])}/${parseInt(m[3])}/${m[1].slice(2)}`;
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear() % 100}`;
};
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekday = (dateStr) => {
  const m = typeof dateStr === 'string' && dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateStr);
  return isNaN(d.getTime()) ? '' : WEEKDAYS[d.getDay()];
};
const toDateInput = (dateStr) => {
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const TripHistory = ({ user, token, api }) => {
  const [year, setYear] = useState(() => {
    const v = localStorage.getItem('milewise_history_year');
    return v ? parseInt(v) : new Date().getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const v = localStorage.getItem('milewise_history_month');
    return v ? parseInt(v) : new Date().getMonth() + 1;
  });
  const [data, setData] = useState(null);
  const [reimbursement, setReimbursement] = useState(null);
  const [error, setError] = useState('');
  const [editingTripId, setEditingTripId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchSummary();
  }, [year, month]);

  // Remember the selected month so a refresh stays on it
  useEffect(() => {
    localStorage.setItem('milewise_history_year', String(year));
    localStorage.setItem('milewise_history_month', String(month));
  }, [year, month]);

  const fetchSummary = async () => {
    try {
      setError('');
      const result = await api.get(`/trips/summary/monthly?year=${year}&month=${month}`, token);
      setData(result);
    } catch (err) {
      setError(err.message);
      return;
    }
    // Reimbursement total for the month (supplementary; its failure shouldn't break the page)
    try {
      const y = parseInt(year), mo = parseInt(month);
      const pad = (n) => String(n).padStart(2, '0');
      const lastDay = new Date(y, mo, 0).getDate();
      const reimb = await api.get(`/reimbursement?start=${y}-${pad(mo)}-01&end=${y}-${pad(mo)}-${pad(lastDay)}`, token);
      setReimbursement(reimb);
    } catch {
      setReimbursement(null);
    }
  };

  const handleDelete = async (tripId) => {
    if (!window.confirm('Delete this entire trip (all of its rows)?')) return;
    try {
      await api.delete(`/trips/${tripId}`, token);
      fetchSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = async (tripId) => {
    try {
      setError('');
      const detail = await api.get(`/trips/${tripId}`, token);
      setEditForm({
        trip_id: tripId,
        trip_date: toDateInput(detail.trip_date),
        origin: (detail.legs && detail.legs.length) ? detail.legs[0].from_id : 'HOM',
        legs: (detail.legs || []).map(l => ({
          from_id: l.from_id,
          to_id: l.to_id,
          from_address: l.from_address || '',
          to_address: l.to_address || '',
          distance: String(l.distance),
          billable_time: String(l.billable_time),
          unbillable_time: String(l.unbillable_time),
          cost: String(l.cost),
          description: l.description || ''
        })),
        no_mileage_entries: (detail.no_mileage_entries || []).map(n => ({
          customer_id: n.customer_id,
          billable_time: String(n.billable_time),
          unbillable_time: String(n.unbillable_time),
          cost: String(n.cost),
          description: n.description || ''
        }))
      });
      setEditingTripId(tripId);
    } catch (err) {
      setError(err.message);
    }
  };

  const cancelEdit = () => {
    setEditingTripId(null);
    setEditForm(null);
  };

  // Recompute every leg's mileage from the customer addresses (same engine as Process Trip)
  const handleRecalc = async () => {
    setRecalcing(true);
    setError('');
    try {
      const resp = await api.post('/trips/recalc-mileage', {
        legs: editForm.legs.map(l => ({ from_id: l.from_id, to_id: l.to_id, from_address: l.from_address, to_address: l.to_address }))
      }, token);
      setEditForm(f => {
        if (resp.legs.length !== f.legs.length) return f; // legs changed during recalc; ignore stale result
        return { ...f, legs: f.legs.map((l, i) => resp.legs[i].distance != null ? { ...l, distance: String(resp.legs[i].distance) } : l) };
      });
      if (resp.failed && resp.failed.length) {
        setError('Could not compute: ' + resp.failed.join(', ') + ' — those legs kept their current mileage.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRecalcing(false);
    }
  };

  const updateDate = (value) => setEditForm(f => ({ ...f, trip_date: value }));

  const blankLeg = () => ({ from_id: '', to_id: '', from_address: '', to_address: '', distance: '0', billable_time: '0', unbillable_time: '0', cost: '0', description: '' });

  // Rebuild the route chain so each leg's origin is the previous leg's destination
  const rechain = (legs, origin) => legs.map((leg, i) => ({ ...leg, from_id: i === 0 ? origin : legs[i - 1].to_id }));

  const updateLeg = (i, field, value) => setEditForm(f => {
    let legs = f.legs.map((l, idx) => idx === i ? { ...l, [field]: value } : l);
    if (field === 'to_id') legs = rechain(legs, f.origin);
    return { ...f, legs };
  });

  const addLeg = () => setEditForm(f => ({ ...f, legs: rechain([...f.legs, blankLeg()], f.origin) }));

  const removeLeg = (i) => setEditForm(f => ({ ...f, legs: rechain(f.legs.filter((_, idx) => idx !== i), f.origin) }));

  const moveLeg = (i, dir) => setEditForm(f => {
    const j = i + dir;
    if (j < 0 || j >= f.legs.length) return f;
    const legs = [...f.legs];
    [legs[i], legs[j]] = [legs[j], legs[i]];
    return { ...f, legs: rechain(legs, f.origin) };
  });

  const updateNM = (i, field, value) => setEditForm(f => ({
    ...f,
    no_mileage_entries: f.no_mileage_entries.map((n, idx) => idx === i ? { ...n, [field]: value } : n)
  }));

  const saveEdit = async () => {
    if (editForm.legs.some(l => !l.to_id || !l.to_id.trim())) {
      setError('Every leg needs a Customer (use HOM for the leg returning home).');
      return;
    }
    setSaving(true);
    try {
      setError('');
      await api.put(`/trips/${editForm.trip_id}`, {
        trip_date: editForm.trip_date,
        legs: editForm.legs,
        no_mileage_entries: editForm.no_mileage_entries
      }, token);
      cancelEdit();
      fetchSummary();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Group itemized rows by trip, preserving order
  const groupByTrip = (rows) => {
    const groups = [];
    let cur = null;
    for (const row of rows) {
      if (!cur || cur.tripId !== row.trip_id) {
        cur = { tripId: row.trip_id, rows: [] };
        groups.push(cur);
      }
      cur.rows.push(row);
    }
    return groups;
  };

  const routeSummary = (group) => {
    const stops = group.rows.filter(r => r.customer !== 'HOM').map(r => r.customer);
    return stops.length ? stops.join(' → ') : 'HOM';
  };

  const groups = data ? groupByTrip(data.rows) : [];

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-4">
        <Select value={year} onChange={e => setYear(e.target.value)} className="w-28">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Select>
        <Select value={month} onChange={e => setMonth(e.target.value)} className="w-40">
          {MONTHS.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
        </Select>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Trip History - {MONTHS[month]} {year}</CardTitle>
        </CardHeader>
        <CardContent>
          {!data || data.rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No data for this period.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-2 pl-4">Customer</th>
                    <th className="pb-2">Billable (hrs)</th>
                    <th className="pb-2">Unbillable (hrs)</th>
                    <th className="pb-2">Mileage</th>
                    <th className="pb-2">Cost</th>
                    <th className="pb-2">Description</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(group => {
                    const isEditing = editingTripId === group.tripId && editForm;
                    const tripMiles = group.rows.reduce((a, r) => a + parseFloat(r.mileage || 0), 0);
                    return (
                      <Fragment key={group.tripId}>
                        {/* Trip header: date + route on the left, actions on the right */}
                        <tr className="bg-muted/40 border-t-2 border-border">
                          <td colSpan={7} className="py-2 px-1">
                            <div className="flex items-center justify-between gap-2">
                              {isEditing ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">Date:</span>
                                  <Input type="date" value={editForm.trip_date} onChange={e => updateDate(e.target.value)} className="h-8 w-44" />
                                </div>
                              ) : (
                                <div className="font-medium">
                                  {weekday(group.rows[0].trip_date)}, {formatDate(group.rows[0].trip_date)}
                                  <span className="text-muted-foreground font-normal"> · {routeSummary(group)}</span>
                                  <span className="font-normal"> · {tripMiles.toFixed(1)} mi</span>
                                </div>
                              )}
                              <div className="flex gap-1">
                                {isEditing ? (
                                  <>
                                    <Button variant="secondary" size="sm" onClick={handleRecalc} disabled={saving || recalcing}>{recalcing ? 'Recalculating…' : 'Recalculate mileage'}</Button>
                                    <Button size="sm" onClick={saveEdit} disabled={saving || recalcing}>{saving ? 'Saving...' : 'Save'}</Button>
                                    <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving || recalcing}>Cancel</Button>
                                  </>
                                ) : (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => startEdit(group.tripId)}>Edit</Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(group.tripId)}>Delete</Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>

                        {/* Rows: read-only or editable */}
                        {!isEditing && group.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-border/50">
                            <td className="py-1 pl-4">{row.customer}</td>
                            <td className="py-1">{parseFloat(row.billable_time).toFixed(1)}</td>
                            <td className="py-1">{parseFloat(row.unbillable_time).toFixed(1)}</td>
                            <td className="py-1">{parseFloat(row.mileage).toFixed(1)}</td>
                            <td className="py-1">${parseFloat(row.cost).toFixed(2)}</td>
                            <td className="py-1 text-muted-foreground">{row.description}</td>
                            <td className="py-1"></td>
                          </tr>
                        ))}

                        {isEditing && editForm.legs.map((leg, i) => (
                          <tr key={`leg-${i}`} className="border-b border-border/50">
                            <td className="py-1 pl-4"><Input value={leg.to_id} onChange={e => updateLeg(i, 'to_id', e.target.value.toUpperCase())} className="h-8 w-24" /></td>
                            <td className="py-1"><Input value={leg.billable_time} onChange={e => updateLeg(i, 'billable_time', numFilter(e.target.value))} className="h-8 w-20" /></td>
                            <td className="py-1"><Input value={leg.unbillable_time} onChange={e => updateLeg(i, 'unbillable_time', numFilter(e.target.value))} className="h-8 w-20" /></td>
                            <td className="py-1"><Input value={leg.distance} onChange={e => updateLeg(i, 'distance', numFilter(e.target.value))} className="h-8 w-20" /></td>
                            <td className="py-1"><Input value={leg.cost} onChange={e => updateLeg(i, 'cost', numFilter(e.target.value))} className="h-8 w-24" /></td>
                            <td className="py-1"><Input value={leg.description} onChange={e => updateLeg(i, 'description', e.target.value)} className="h-8 w-full" /></td>
                            <td className="py-1 whitespace-nowrap text-right">
                              <Button variant="ghost" size="sm" onClick={() => moveLeg(i, -1)} disabled={i === 0} title="Move up">↑</Button>
                              <Button variant="ghost" size="sm" onClick={() => moveLeg(i, 1)} disabled={i === editForm.legs.length - 1} title="Move down">↓</Button>
                              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeLeg(i)} title="Remove leg">✕</Button>
                            </td>
                          </tr>
                        ))}
                        {isEditing && (
                          <tr>
                            <td colSpan={7} className="py-1 pl-4">
                              <Button variant="outline" size="sm" onClick={addLeg}>+ Add leg</Button>
                              <span className="text-xs text-muted-foreground ml-2">New legs are added at the bottom — use ↑ to move into position.</span>
                            </td>
                          </tr>
                        )}
                        {isEditing && editForm.no_mileage_entries.map((nm, i) => (
                          <tr key={`nm-${i}`} className="border-b border-border/50">
                            <td className="py-1 pl-4"><Input value={nm.customer_id} onChange={e => updateNM(i, 'customer_id', e.target.value.toUpperCase())} className="h-8 w-24" /></td>
                            <td className="py-1"><Input value={nm.billable_time} onChange={e => updateNM(i, 'billable_time', numFilter(e.target.value))} className="h-8 w-20" /></td>
                            <td className="py-1"><Input value={nm.unbillable_time} onChange={e => updateNM(i, 'unbillable_time', numFilter(e.target.value))} className="h-8 w-20" /></td>
                            <td className="py-1 text-muted-foreground text-xs">no mileage</td>
                            <td className="py-1"><Input value={nm.cost} onChange={e => updateNM(i, 'cost', numFilter(e.target.value))} className="h-8 w-24" /></td>
                            <td className="py-1"><Input value={nm.description} onChange={e => updateNM(i, 'description', e.target.value)} className="h-8 w-full" /></td>
                            <td className="py-1"></td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>

              <div className="border-t mt-3 pt-3 space-y-1 text-sm font-semibold">
                <div>Total Distance: {data.totals.distance.toFixed(1)} miles</div>
                <div>Total Billable Time: {data.totals.billable_time.toFixed(1)} hours</div>
                <div>Total Unbillable Time: {data.totals.unbillable_time.toFixed(1)} hours</div>
                <div>Total Cost: ${data.totals.cost.toFixed(2)}</div>
                {reimbursement && (
                  reimbursement.missing_years && reimbursement.missing_years.length > 0
                    ? <div className="text-destructive">Total Reimbursement: no rate set for {reimbursement.missing_years.join(', ')} (set it in the Reimbursement tab)</div>
                    : <div>Total Reimbursement: ${reimbursement.total_amount.toFixed(2)}</div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TripHistory;
